/**
 * Jotai atom definitions for the IntegrationListing feature.
 *
 * Design principles:
 *  - Storage atoms (atomWithStorage) persist user preferences across sessions.
 *  - Reset atoms (atomWithReset) can be snapped back to defaults.
 *  - Derived atoms (atom(get => …)) compute query params from primitive atoms
 *    so components always react to the minimal, correct dependency set.
 */

import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import { isValidDateValue } from 'views/bpaUrlFilterUtils';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import {
  DateRangeFilter,
  FilterValues,
  IntegrationInsightsData,
  IntegrationItem,
  IntegrationListingData,
  IntegrationOptionsData,
  IntegrationQueryParams,
  PaginationConfig,
} from './types/integrationListing.types';
import {
  BPA_INTEGRATION_DATE_FILTER_KEYS,
  getDefaultBPAIntegrationFilters,
} from './urlFilters';

// ---------------------------------------------------------------------------
// UI / layout
// ---------------------------------------------------------------------------

/** Height (px) of the listing component — used by the skeleton loader */
export const componentHeightAtom = atom(0);

/** visibility state for the IntegrationCards new integration panel */
export const isIntegrationCardsVisibleAtom = atom(false);

// ---------------------------------------------------------------------------
// Column visibility
// ---------------------------------------------------------------------------

export const DEFAULT_ACTIVE_INTEGRATION_COLUMN_KEYS = [
  'provider_code',
  'module_code',
  'integration_type',
  'account_id',
  'integration_label',
  'region',
  'active_scan_count',
  'active_finding_count',
  'last_sync_at',
  'actions',
];

export const activeColumnKeysAtom = atomWithStorage<string[]>(
  'bpa_integration_listing_column_keys',
  DEFAULT_ACTIVE_INTEGRATION_COLUMN_KEYS,
);

export const DEFAULT_DELETED_INTEGRATION_COLUMN_KEYS = [
  'provider_code',
  'module_code',
  'integration_type',
  'account_id',
  'integration_label',
  'region',
  'open_vuln_counts_at_deletion',
  'closed_vuln_counts_at_deletion',
  'delete_at',
  'delete_by',
  'actions',
];

export const deletedColumnKeysAtom = atomWithStorage<string[]>(
  'bpa_deleted_integration_listing_column_keys',
  DEFAULT_DELETED_INTEGRATION_COLUMN_KEYS,
);

export const DEFAULT_ALL_INTEGRATION_COLUMN_KEYS = [
  'provider_code',
  'module_code',
  'integration_type',
  'account_id',
  'integration_label',
  'region',
  'status',
  'active_scan_count',
  'active_finding_count',
  'last_sync_at',
  'open_vuln_counts_at_deletion',
  'closed_vuln_counts_at_deletion',
  'delete_at',
  'delete_by',
  'actions',
];

export const allColumnKeysAtom = atomWithStorage<string[]>(
  'bpa_all_integration_listing_column_keys',
  DEFAULT_ALL_INTEGRATION_COLUMN_KEYS,
);

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

/**
 * Raw filter values as entered/selected by the user.
 */
export const filterValuesAtom = atomWithReset<FilterValues>(
  getDefaultBPAIntegrationFilters(),
);

/** Guard so the grid only fetches AFTER query-string filters are hydrated */
export const isQuerystringFiltersInitializedAtom = atom(false);

/** Date filter keys that need Date-object reconstruction from storage */
const DATE_FILTER_KEYS: readonly string[] = [
  ...BPA_INTEGRATION_DATE_FILTER_KEYS,
];

/**
 * Derived atom: rebuilds Date objects from serialised date-range values.
 * All other filter values pass through unchanged.
 */
export const parsedFilterValuesAtom = atom<FilterValues>((get) => {
  const filterValues = get(filterValuesAtom);
  const parsed: FilterValues = { ...filterValues };

  DATE_FILTER_KEYS.forEach((key) => {
    if (parsed[key]) {
      parsed[key] = (parsed[key] as DateRangeFilter[]).map((range) => ({
        from: range.from ? new Date(range.from) : null,
        to: range.to ? new Date(range.to) : null,
      }));
    }
  });

  return parsed;
});

// ---------------------------------------------------------------------------
// Pagination & sorting
// ---------------------------------------------------------------------------

export const paginationConfigAtom = atomWithStorage<PaginationConfig>(
  'bpa_integration_listing_pagination',
  { page: 1, pageSize: DEFAULT_PAGE_SIZE },
);

export const orderByAtom = atomWithReset<string>('created_at');

export const sortOrderAtom = atomWithReset<'asc' | 'desc'>('desc');

// ---------------------------------------------------------------------------
// Row selection
// ---------------------------------------------------------------------------

export const selectedRowIdsAtom = atomWithReset<number[]>([]);

// ---------------------------------------------------------------------------
// Refresh trigger
// ---------------------------------------------------------------------------

/** Changing this atom value forces the grid to re-fetch */
export const integrationDataRefreshAtom = atomWithStorage<string>(
  'bpa_integration_listing_refresh',
  '',
);

/**
 * Immutably updates a specific integration row in the listing data.
 */
export const updateIntegrationRowAtom = atom(
  null,
  (get, set, updater: { id: number; data: Partial<IntegrationItem> }) => {
    const data = get(integrationDataAtom);
    const results = data.results.map((item) =>
      item.id === updater.id ? { ...item, ...updater.data } : item,
    );
    set(integrationDataAtom, { ...data, results });
  },
);

/**
 * Immutably updates multiple integration rows in the listing data.
 */
export const updateIntegrationRowsAtom = atom(
  null,
  (get, set, updater: { ids: number[]; data: Partial<IntegrationItem> }) => {
    const data = get(integrationDataAtom);
    const results = data.results.map((item) =>
      updater.ids.includes(item.id) ? { ...item, ...updater.data } : item,
    );
    set(integrationDataAtom, { ...data, results });
  },
);

// ---------------------------------------------------------------------------
// Data atoms
// ---------------------------------------------------------------------------

/** Holds the current page of integration records and loading/error state */
export const integrationDataAtom = atom<IntegrationListingData>({
  results: [],
  count: 0,
  loading: false,
});

/** Holds the dynamically fetched integration options (providers, modules, regions, benchmarks) */
export const integrationOptionsDataAtom = atom<IntegrationOptionsData>({
  providers: [],
  modules: [],
  regions: [],
  benchmarks: [],
});

/** Holds the server-computed insights from GET /bpa/integrations/insights/ */
export const integrationInsightsAtom = atom<IntegrationInsightsData>({
  total_integrations: 0,
  active_integrations: 0,
  aging_integrations: 0,
  aged_out_integration: 0,
  provider_count: 0,
  module_count: 0,
  region_count: 0,
  loading: false,
});

// ---------------------------------------------------------------------------
// Derived query-param atoms
// ---------------------------------------------------------------------------

/**
 * Builds the full set of query parameters for GET /bpa/integrations/.
 * This is the single source of truth consumed by the data-grid hook.
 */
export const integrationQueryParamsAtom = atom<IntegrationQueryParams>(
  (get) => {
    const companyId = get(companyIdAtom);
    const pagination = get(paginationConfigAtom);
    const orderBy = get(orderByAtom);
    const sortOrder = get(sortOrderAtom);
    const filterValues = get(parsedFilterValuesAtom);
    // reading this atom makes the grid react to manual refresh triggers
    void get(integrationDataRefreshAtom);

    const params: IntegrationQueryParams = {
      company_id: companyId,
      page: pagination.page,
      page_size: pagination.pageSize,
      ordering: sortOrder === 'desc' ? `-${orderBy}` : orderBy,
    };

    // Spread filter values into query params, converting arrays to
    // comma-joined strings (matching the existing AccountListing pattern).
    Object.entries(filterValues).forEach(([key, value]) => {
      if (!value) return;

      if (DATE_FILTER_KEYS.includes(key)) {
        // date-range filter: send as ISO strings
        const [range] = value as DateRangeFilter[];
        if (isValidDateValue(range?.from)) {
          params[`${key}_after`] = range.from.toISOString();
        }
        if (isValidDateValue(range?.to)) {
          params[`${key}_before`] = range.to.toISOString();
        }
      } else if (Array.isArray(value) && value.length > 0) {
        params[key] = value.join(',');
      } else if (typeof value === 'string' && value.length > 0) {
        params[key] = value;
      }
    });

    return params;
  },
);

// ---------------------------------------------------------------------------
// Helper hooks
// ---------------------------------------------------------------------------

/**
 * Returns a callback that resets pagination to page 1 and clears selected
 * rows. Call this whenever the user changes a filter.
 */
export function useOnFilterChangeResetRelatedAtoms() {
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const resetSelectedRows = useResetAtom(selectedRowIdsAtom);

  return () => {
    resetSelectedRows();
    // Only write when the page actually needs to change; writing the same
    // value would still create a new object reference and trigger a second
    // recomputation of integrationQueryParamsAtom → second API call.
    if (paginationConfig.page !== 1) {
      setPaginationConfig({ ...paginationConfig, page: 1 });
    }
  };
}

/**
 * Returns a callback that resets ALL IntegrationListing atoms to their
 * default values (used by the dashboard-level reset button).
 */
export function useResetAllIntegrationListingAtoms() {
  const resetFilterValues = useResetAtom(filterValuesAtom);
  const resetSelectedRows = useResetAtom(selectedRowIdsAtom);
  const resetOrderBy = useResetAtom(orderByAtom);
  const resetSortOrder = useResetAtom(sortOrderAtom);
  const setRefresh = useSetAtom(integrationDataRefreshAtom);

  // paginationConfigAtom uses atomWithStorage (user preference), so reset
  // it by writing the default value rather than atomWithReset.
  const setPagination = useSetAtom(paginationConfigAtom);

  return () => {
    resetFilterValues();
    resetSelectedRows();
    resetOrderBy();
    resetSortOrder();
    setPagination({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
    setRefresh(new Date().toISOString());
  };
}

// Re-export the type so consumers don't need to dig into the types folder
export type { IntegrationItem };
